// Avantages validés par QR code (D-156), côté partenaire, bureau et bénévole (maquettes b1 à b7, c1, c2, d1).
//  - La page ouverte par l'appareil photo (/v/<jeton>, sur l'adresse du club, sans application) : la page dépend de qui
//    tient le téléphone. Reconnu (compte partenaire connecté, lien privé déjà ouvert sur ce téléphone, bureau, bénévole de
//    service) : le prénom du membre pour vérifier, l'avantage, ce qu'il reste, « Valider » (2 minutes). Pas reconnu : ni
//    prénom ni stock ; « Recevoir le lien de mon espace » (à l'adresse connue du club) ou « Me connecter ». Puis « Déjà
//    utilisé », « QR expiré », « Épuisé » (D-131), « Pas de service en ce moment » (sans le prénom). Vouvoiement pour le
//    partenaire, tutoiement pour le bureau et les bénévoles (des membres).
//  - « Valider un avantage » en tête de l'espace du partenaire (compte ou lien privé) : scanner avec l'appareil photo, ou
//    « Taper un code » (le code à 6 chiffres sous le QR du membre).
//  - Ses validations (Visibilité, ou son lien privé) : l'heure, la façon, qui a validé chez lui, le code ; jamais le nom du
//    membre (D-044).
import './perk-qr.css';
import {
  codeCheck, codeValidate, partnerLink, scanPass, sendSpaceLink, validatePass, type PartnerValidations, type ScanResult,
} from '../lib/perk-qr-api';
import { cleanCode, HOW_LABEL, lowerPost, ofClubName, partnerWho } from '../domain/perk-qr';
import { stockLine } from '../domain/perk-stock';
import { FREQ_LABEL } from '../domain/pictos';
import { applyBrand } from './theme';
import { partnerMark } from './partners';
import { picto } from './perks';
import { time } from './format';
import { $, $$, html, mount, openSheet, raw, type Html } from './dom';

const ICO = {
  check: raw('<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>'),
  again: raw('<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>'),
  empty: raw('<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 8h16l-1.5 11a2 2 0 0 1-2 1.7h-9a2 2 0 0 1-2-1.7z"/><path d="M9 8V6a3 3 0 0 1 6 0v2M9.5 13.5l5 5M14.5 13.5l-5 5"/></svg>'),
  lock: raw('<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="10.5" width="14" height="10" rx="2.5"/><path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5"/></svg>'),
};
const bar = (used: number, total: number): Html =>
  html`<span class="pk-bar" role="img" aria-label="${used} sur ${total}"><i style="width:${Math.min(100, Math.round(used / Math.max(total, 1) * 100))}%"></i></span>`;
const at = (iso: string | null | undefined) => time(iso ? new Date(iso) : new Date());

/** La page du scan : /v/<jeton>. `appBase` : l'app du club (/app sur son adresse, sinon /). */
export async function renderPerkScan(root: HTMLElement, token: string, appBase: string): Promise<void> {
  const robots = document.createElement('meta');
  robots.name = 'robots'; robots.content = 'noindex, nofollow';
  document.head.append(robots);
  document.title = 'Valider un avantage';
  const link = partnerLink();
  let r: ScanResult;
  try { r = await scanPass(token, link); }
  catch {
    mount(root, html`<main class="pub-missing"><h1>Pas de réseau</h1><p>La validation demande le réseau. Sans réseau, le membre peut garder le doigt 5 secondes sur son écran.</p>
      <button type="button" class="btn btn-primary" id="qx-retry">Réessayer</button></main>`);
    $(root, '#qx-retry')!.addEventListener('click', () => void renderPerkScan(root, token, appBase));
    return;
  }
  if (r.club) applyBrand({ id: 'perk-scan', name: r.club.name, short_name: r.club.short, logo_url: r.club.logo, primary_color: r.club.color }, false);
  show(root, r, { token, link, appBase });
}

interface Ctx { token: string | null; link: string | null; appBase: string; code?: string }

/** Une page de résultat (scan, validation, code). */
function show(root: HTMLElement, r: ScanResult, c: Ctx): void {
  if (r.state === 'invalid' || !r.club || !r.perk) {
    mount(root, html`<main class="pub-missing"><h1>QR inconnu</h1><p>Ce QR ne vient pas d’un avantage du club, ou il a été retiré. Demandez au membre d’ouvrir l’avantage dans son app.</p></main>`);
    return;
  }
  mount(root, html`<div class="pr qx-v">
    <header class="pr-head">${r.club.logo ? html`<img class="pr-club" src="${r.club.logo}" alt="">` : ''}
      <div><strong>${r.club.name}</strong><span class="pr-kicker">Valider un avantage</span></div></header>
    <main class="pub-main">${body(r, c)}</main></div>`);
  wire(root, r, c, (next) => show(root, next, c));
}

/** Vouvoiement pour le partenaire, tutoiement pour le bureau et les bénévoles. */
const vous = (r: ScanResult) => !!r.perk?.partner;

function meLine(r: ScanResult): Html | '' {
  const v = r.validator;
  if (!v?.via && r.state !== 'no_service') return '';
  if (r.perk!.partner) {
    return html`<p class="qx-me">${partnerMark({ name: r.perk!.partner, logo: r.perk!.partner_logo }, 'pt-logo')}<span>${r.perk!.partner}
      <small>${v?.via === 'link' ? 'Reconnu : votre lien privé, ouvert sur ce téléphone' : `Reconnu : votre compte partenaire${v?.name ? ` (${v.name})` : ''}`}</small></span>
      <span class="chip chip-ok" aria-label="Reconnu">✓</span></p>`;
  }
  const name = v?.name ?? '';
  return html`<p class="qx-me"><span class="qx-av" aria-hidden="true">${(name || '?').charAt(0).toUpperCase()}</span><span>${name ? `Salut ${name}` : 'Salut'}
    <small>${r.state === 'no_service' ? 'Pas de créneau en cours pour toi' : v?.via === 'bureau' ? 'Tu es au bureau du club : tu peux valider.'
      : `Tu es de service ${lowerPost(v?.post ?? '')} : tu peux valider.`}</small></span>
    ${r.state === 'no_service' ? '' : html`<span class="chip chip-ok" aria-label="Reconnu">✓</span>`}</p>`;
}

const mini = (r: ScanResult, small: string, chip: Html | '' = '', off = false, withName = true): Html => html`
  <div class="card qx-mini"><span class="pk-ico ${off ? 'off' : ''}">${picto(r.perk!.picto, 22)}</span>
    <span><strong>${withName && r.member ? `${r.member} · ` : ''}${r.perk!.title}</strong><small>${small}</small></span>${chip}</div>`;

function follow(r: ScanResult, c: Ctx): Html | '' {
  const via = r.validator?.via;
  if (via === 'link' && c.link) return html`<a class="btn btn-ghost" href="/p/${c.link}">Voir mes validations</a>`;
  if (via === 'partner') return html`<a class="btn btn-ghost" href="${c.appBase.replace(/\/$/, '')}/#/visibilite">Voir mes validations</a>`;
  if (via === 'bureau') return html`<a class="btn btn-ghost" href="${c.appBase.replace(/\/$/, '')}/#/avantages/reglages/validations">Voir les validations</a>`;
  return '';
}

function blockText(r: ScanResult): string {
  const m = r.member ?? 'Ce membre';
  switch (r.block) {
    case 'used_today': return `${m} a déjà utilisé cet avantage aujourd’hui.`;
    case 'used_month': return `${m} a déjà utilisé cet avantage ce mois-ci.`;
    case 'used_once': return `${m} l’a déjà eu : 1 fois par personne.`;
    case 'no_match_today': return 'Cet avantage vaut les jours de match seulement.';
    case 'no_win': return 'Pas de victoire qui ouvre droit à cet avantage en ce moment.';
    default: return 'Cet avantage n’est plus proposé.';
  }
}

function body(r: ScanResult, c: Ctx): Html {
  const p = r.perk!, club = r.club!, V = vous(r);
  const t = (tu: string, vs: string) => (V ? vs : tu);
  const stock = r.stock ?? null, used = r.stock_used ?? 0;
  switch (r.state) {
    case 'ready': {
      const left = stock != null ? Math.max(stock - used, 0) : null;
      return html`${meLine(r)}
        <section class="card qx-vcard">
          <p class="pub-kicker">${p.partner ? `Membre ${ofClubName(club.name)}` : `Membre du club${r.team ? ` · ${r.team}` : ''}`}</p>
          <h1 class="qx-first">${r.member ?? ''}</h1>
          <div class="qx-perk"><span class="pk-ico">${picto(p.picto, 24)}</span><span><strong>${p.title}</strong>
            <small>${p.partner ? 'Votre offre' : 'Avantage du club'} · ${FREQ_LABEL[p.frequency]}</small></span></div>
          ${stock != null ? html`<div class="qx-stock">${bar(used, stock)}<span>${stockLine(used, stock)}</span></div>` : ''}
        </section>
        <button type="button" class="btn btn-primary qx-go" id="qx-go" data-guide="qx-go">Valider</button>
        <p class="hint">${`Valider compte l’avantage${left != null ? ` : il en restera ${Math.max(left - 1, 0)}` : ''}. `}${p.partner
          ? `${r.member} voit « Validé » sur son téléphone, tout de suite.` : `Remets-lui l’avantage ; ${r.member} voit « Validé » sur son téléphone.`}</p>
        <p class="qx-small">${p.partner ? `${c.code ? 'Code reconnu' : 'QR scanné'} à ${at(r.scanned_at)} · à valider dans les 2 minutes.` : 'Le bureau voit qui a validé, et quand.'}</p>
        <p class="form-msg" role="alert" id="qx-err"></p>`;
    }
    case 'done':
      return html`${meLine(r)}
        <section class="qx-state" role="status"><span class="qx-badge ok">${ICO.check}</span><h2>Validé</h2>
          <p><b>${r.member ?? ''}</b> · ${p.title}<br>aujourd’hui à ${at(r.used_at)}</p></section>
        ${stock != null ? html`<section class="card qx-stock">${bar(used, stock)}<span>${stockLine(used, stock)}</span></section>` : ''}
        <p class="hint">${p.partner ? `Appliquez l’avantage : il est compté, et ${r.member} a vu « Validé » sur son téléphone. Client suivant : scannez son QR avec l’appareil photo.`
          : `C’est compté : remets-lui l’avantage. ${r.member} a vu « Validé » sur son téléphone.`}</p>
        ${follow(r, c)}`;
    case 'unknown':
      return html`<section class="qx-state"><span class="qx-badge mute">${ICO.lock}</span><h2>Téléphone pas reconnu</h2>
          <p>Cet avantage est offert par <b>${p.partner ?? ''}</b>. Pour le valider, ouvrez une fois votre espace partenaire sur ce téléphone : ensuite, il est reconnu.</p></section>
        ${mini(r, `Offre de ${p.partner ?? ''} aux membres ${ofClubName(club.name)}`, '', false, false)}
        <div class="qx-two">
          ${c.token && r.mail_hint ? html`<button type="button" class="btn btn-primary" id="qx-send">Recevoir le lien de mon espace</button>
            <p class="qx-sub" id="qx-sent">Par e-mail, à ${r.mail_hint} (l’adresse connue du club)</p>`
            : html`<p class="qx-sub">Le club n’a pas votre adresse e-mail : demandez-lui le lien de votre espace partenaire.</p>`}
          <a class="btn btn-ghost" href="${c.appBase}">Me connecter</a>
          <p class="qx-sub">Avec votre compte partenaire</p>
        </div>
        <p class="hint">Puis scannez de nouveau le QR du membre : il change toutes les 30 secondes.</p>
        <hr class="qx-sep"><p class="qx-small">Vous n’êtes pas le commerçant ? Rien à faire ici : ce QR se montre chez ${p.partner ?? ''}.</p>`;
    case 'login':
      return html`<section class="qx-state"><span class="qx-badge mute">${ICO.lock}</span><h2>Pas reconnu</h2>
          <p>Un avantage du club se valide par le bureau, ou par un bénévole de service connecté à l’app sur ce téléphone.</p></section>
        ${mini(r, 'Avantage du club', '', false, false)}
        <a class="btn btn-primary" href="${c.appBase}">Me connecter</a>
        <p class="hint">Puis scanner de nouveau le QR du membre : il change toutes les 30 secondes.</p>`;
    case 'no_service':
      return html`${meLine(r)}
        <section class="qx-state"><span class="qx-badge mute">${ICO.lock}</span><h2>Pas de service en ce moment</h2>
          <p>Un avantage du club se valide par le bureau ou par un bénévole de service : la buvette, l’accueil, pendant son créneau.</p></section>
        ${mini(r, 'Avantage du club', '', false, false)}
        <p class="hint">Rien à faire ici : c’est la personne de service qui le scanne.</p>`;
    case 'used':
      return html`${meLine(r)}
        <section class="qx-state"><span class="qx-badge warn">${ICO.again}</span><h2>Déjà utilisé</h2>
          <p>Ce QR a été validé${r.used_at ? html` à <b>${at(r.used_at)}</b>` : ''}${r.by_me ? t(', par toi', ', par vous') : ''}. Rien à refaire : c’est compté une fois.</p></section>
        ${mini(r, `Validé${r.used_at ? ` à ${at(r.used_at)}` : ''}${r.used_how ? ` · ${HOW_LABEL[r.used_how]}` : ''}`, html`<span class="chip chip-ok">Validé</span>`)}
        <p class="hint">${t('Un QR ne sert qu’une fois et change toutes les 30 secondes : une capture d’écran ne passe pas. Au moindre doute, demande au membre d’ouvrir l’avantage dans son app, devant toi.',
          'Un QR ne sert qu’une fois et change toutes les 30 secondes : une capture d’écran ne passe pas. Au moindre doute, demandez au membre d’ouvrir l’avantage dans son app, devant vous.')}</p>`;
    case 'expired':
      return html`${meLine(r)}
        <section class="qx-state"><span class="qx-badge warn">${ICO.again}</span><h2>QR expiré</h2>
          <p>Ce QR n’est plus valable : il change toutes les 30 secondes et ne sert qu’une fois. Rien n’est compté.</p></section>
        ${mini(r, 'Pas compté', html`<span class="chip chip-mute">Expiré</span>`)}
        <p class="hint">${t('Demande au membre d’ouvrir l’avantage dans son app, devant toi, puis scanne le nouveau QR.',
          'Demandez au membre d’ouvrir l’avantage dans son app, devant vous, puis scannez le nouveau QR.')}</p>`;
    case 'exhausted': {
      const n = stock ?? used;
      return html`${meLine(r)}
        <section class="qx-state"><span class="qx-badge warn">${ICO.empty}</span><h2>Épuisé</h2>
          <p>${n > 1 ? html`Les <b>${n}</b> ${p.partner ? 'de votre offre' : 'de cet avantage'} sont partis` : 'Le seul était parti'}${r.exhausted_at ? ` : le dernier à ${at(r.exhausted_at)}` : ''}. ${p.partner ? 'Merci pour les membres du club !' : ''}</p></section>
        ${mini(r, `${n} ${n > 1 ? 'partis' : 'parti'} sur ${n}`, html`<span class="chip chip-mute">Épuisé</span>`, true)}
        <p class="hint">${p.partner ? 'Cette remise n’est plus comptée : vous pouvez la refuser. Le club peut la remettre en jeu avec un nouveau nombre, avec votre accord.'
          : 'Il n’est plus compté : ne le remets pas. Le bureau peut le remettre en jeu avec un nouveau nombre.'}</p>
        ${follow(r, c)}`;
    }
    default:
      return html`${meLine(r)}
        <section class="qx-state"><span class="qx-badge mute">${ICO.lock}</span><h2>Pas disponible</h2><p>${blockText(r)}</p></section>
        ${mini(r, FREQ_LABEL[p.frequency])}
        <p class="hint">Rien n’est compté.</p>`;
  }
}

function wire(root: HTMLElement, r: ScanResult, c: Ctx, next: (r: ScanResult) => void): void {
  const go = $<HTMLButtonElement>(root, '#qx-go');
  go?.addEventListener('click', async () => {
    go.disabled = true;
    go.textContent = '…';
    try {
      const out = c.code ? await codeValidate(c.code, c.link) : await validatePass(c.token!, c.link);
      try { if (out.state === 'done') navigator.vibrate?.([60, 40, 120]); } catch { /* sans vibreur */ }
      next(out.club ? out : { ...r, ...out, club: r.club, perk: r.perk });
      window.scrollTo(0, 0);
    } catch {
      go.disabled = false;
      go.textContent = 'Valider';
      const e = $(root, '#qx-err'); if (e) e.textContent = vous(r) ? 'Pas de réseau : réessayez.' : 'Pas de réseau : réessaie.';
    }
  });
  const send = $<HTMLButtonElement>(root, '#qx-send');
  send?.addEventListener('click', async () => {
    send.disabled = true;
    const sub = $(root, '#qx-sent');
    try {
      const s = await sendSpaceLink(c.token!);
      send.textContent = s.sent ? 'Lien envoyé' : 'Envoi impossible';
      if (sub) sub.textContent = s.sent ? `Envoyé à ${s.hint ?? r.mail_hint ?? ''} : ouvrez-le sur ce téléphone, puis scannez de nouveau le QR du membre.`
        : 'Le club n’a pas d’adresse où vous écrire : demandez-lui le lien de votre espace partenaire.';
    } catch { send.disabled = false; if (sub) sub.textContent = 'Pas de réseau : réessayez.'; }
  });
}

// -----------------------------------------------------------------------------------------------------------------------
// Chez le partenaire : la carte « Valider un avantage » (maquette b6) et « Taper le code » (b7)
// -----------------------------------------------------------------------------------------------------------------------
/** La carte en tête de l'espace du partenaire : scanner avec l'appareil photo, ou taper le code. `link` : son lien privé
 *  (espace sans compte), sinon son compte. */
export function validateCard(v: PartnerValidations | null, followHref: string | null): Html {
  const k = v?.perks.find(x => x.active) ?? null;
  const today = v?.perks.reduce((n, x) => n + x.today, 0) ?? 0;
  const offer = k?.stock != null ? (k.exhausted_at ? 'votre offre : épuisée' : `votre offre : plus que ${Math.max(k.stock - k.stock_used, 0)} sur ${k.stock}`) : '';
  return html`<section class="card qx-vh" data-guide="qx-card">
    <div class="qx-vh-h"><span class="qx-vh-ic"><span class="ti ti-qrcode" aria-hidden="true"></span></span>
      <span><strong>Valider un avantage</strong><small>Scannez le QR du membre avec l’appareil photo de ce téléphone : rien à installer.</small></span></div>
    <div class="btn-row"><button type="button" class="btn btn-primary btn-sm" data-qx-code data-guide="qx-code-btn">Taper un code</button>
      ${followHref ? html`<a class="btn btn-ghost btn-sm" href="${followHref}">Mes validations</a>` : ''}</div>
    <p class="qx-small">Aujourd’hui : ${today} ${today > 1 ? 'validés' : 'validé'}${offer ? ` · ${offer}` : ''}</p>
  </section>`;
}

/** « Taper le code » : les 6 chiffres sous le QR du membre ; reconnu, le prénom et l'avantage, puis « Valider ». */
export function openCodeSheet(link: string | null, onDone: () => void): void {
  let changed = false;
  openSheet('Taper le code', html`
    <p class="muted small" style="margin:0">Le code à 6 chiffres affiché sous le QR du membre.</p>
    <label class="qx-otp" aria-label="Code à 6 chiffres">
      ${[0, 1, 2].map(i => html`<span data-d="${i}"></span>`)}<i aria-hidden="true"></i>${[3, 4, 5].map(i => html`<span data-d="${i}"></span>`)}
      <input id="qx-otp" inputmode="numeric" autocomplete="one-time-code" maxlength="7" pattern="[0-9 ]*" aria-label="Code à 6 chiffres">
    </label>
    <p class="qx-msg" role="status" id="qx-msg"></p>
    <div id="qx-found"></div>
    <p class="hint">Le code change avec le QR, toutes les 30 secondes : tapez celui affiché en ce moment.</p>`, (sheet) => {
    const input = $<HTMLInputElement>(sheet, '#qx-otp')!;
    const boxes = $$<HTMLElement>(sheet, '[data-d]');
    const msg = $(sheet, '#qx-msg')!, found = $(sheet, '#qx-found')!;
    let last = '';
    const paint = () => {
      const d = input.value.replace(/\D/g, '').slice(0, 6);
      boxes.forEach((b, i) => { b.textContent = d[i] ?? ''; b.classList.toggle('on', i === Math.min(d.length, 5) && document.activeElement === input); });
      return d;
    };
    const check = async () => {
      const d = paint();
      const code = cleanCode(d);
      if (!code) { if (d !== last) { found.innerHTML = ''; msg.textContent = ''; } last = d; return; }
      if (code === last) return;
      last = code;
      msg.textContent = 'Recherche…';
      try {
        const r = await codeCheck(code, link);
        if (r.state === 'ready') {
          msg.textContent = '';
          mount(found, html`<section class="card qx-found">
              <p class="pub-kicker" style="color:var(--ok)">✓ Code reconnu</p>
              <h1 class="qx-first">${r.member ?? ''}</h1>
              <div class="qx-perk"><span class="pk-ico">${picto(r.perk!.picto, 24)}</span><span><strong>${r.perk!.title}</strong>
                <small>Votre offre${r.stock != null ? ` · plus que ${Math.max(r.stock - (r.stock_used ?? 0), 0)} sur ${r.stock}` : ` · ${FREQ_LABEL[r.perk!.frequency]}`}</small></span></div>
            </section>
            <button type="button" class="btn btn-primary qx-go" id="qx-code-go">Valider</button>`);
          const go = $<HTMLButtonElement>(found, '#qx-code-go')!;
          go.addEventListener('click', async () => {
            go.disabled = true; go.textContent = '…';
            try {
              const out = await codeValidate(code, link);
              changed = changed || out.state === 'done';
              mount(found, outcome(out, r));
              if (out.state === 'done') { input.value = ''; last = ''; paint(); }
            } catch { go.disabled = false; go.textContent = 'Valider'; msg.textContent = 'Pas de réseau : réessayez.'; }
          });
        } else {
          found.innerHTML = '';
          msg.textContent = r.state === 'too_many' ? 'Trop d’essais : attendez quelques minutes.'
            : r.state === 'ambiguous' ? 'Ce code correspond à deux QR : scannez plutôt le QR du membre.'
            : r.state === 'exhausted' ? 'Épuisé : votre offre n’a plus de place, la remise n’est plus comptée.'
            : r.state === 'blocked' ? blockText(r)
            : 'Code inconnu : tapez celui affiché en ce moment sous le QR.';
        }
      } catch { msg.textContent = 'Pas de réseau : réessayez.'; last = ''; }
    };
    input.addEventListener('input', () => void check());
    input.addEventListener('focus', paint);
    input.addEventListener('blur', paint);
    input.focus();
    paint();
  }, () => { if (changed) onDone(); });
}

/** Ce que « Valider » a donné, dans la feuille du code. */
function outcome(out: ScanResult, before: ScanResult): Html {
  const m = out.member ?? before.member ?? '';
  if (out.state === 'done') {
    return html`<section class="qx-state" role="status"><span class="qx-badge ok">${ICO.check}</span><h2>Validé</h2>
      <p><b>${m}</b> · ${before.perk!.title}<br>aujourd’hui à ${at(out.used_at)}</p></section>
      ${out.stock != null ? html`<section class="card qx-stock">${bar(out.stock_used ?? 0, out.stock)}<span>${stockLine(out.stock_used ?? 0, out.stock)}</span></section>` : ''}
      <p class="hint">Appliquez l’avantage : il est compté, et ${m} a vu « Validé » sur son téléphone.</p>`;
  }
  const t = out.state === 'used' ? 'Déjà utilisé : rien à refaire.' : out.state === 'exhausted' ? 'Épuisé : la remise n’est plus comptée.'
    : out.state === 'blocked' ? blockText({ ...out, member: m }) : 'Le code a changé : tapez celui affiché en ce moment.';
  return html`<p class="qx-msg">${t}</p>`;
}

// -----------------------------------------------------------------------------------------------------------------------
// Le suivi du partenaire (maquette d1) : Visibilité, ou son lien privé
// -----------------------------------------------------------------------------------------------------------------------
const fmtWhen = (iso: string) => {
  const d = new Date(iso), now = new Date();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  const day = d.toDateString() === now.toDateString() ? 'Aujourd’hui' : d.toDateString() === y.toDateString() ? 'Hier'
    : d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }).replace(/^./, x => x.toUpperCase());
  return `${day} · ${time(d)}`;
};

export function partnerValidationsBlock(v: PartnerValidations): Html | '' {
  if (!v.perks.length && !v.uses.length) return '';
  const k = v.perks.find(x => x.active) ?? v.perks[0];
  const today = v.perks.reduce((n, x) => n + x.today, 0), month = v.perks.reduce((n, x) => n + x.month, 0);
  const how = (m: keyof typeof HOW_LABEL) => html`<span class="qx-how ${m}">${HOW_LABEL[m]}</span>`;
  return html`<section class="card pr-kpis" data-guide="pa-uses"><p class="eyebrow">Vos validations${v.perks.length === 1 && k ? ` · ${k.title}` : ''}</p>
    <div class="lic-kpis"><span><b>${today}</b><small>aujourd’hui</small></span><span><b>${month}</b><small>ce mois</small></span>
      ${k?.stock != null ? html`<span><b>${Math.max(k.stock - k.stock_used, 0)}</b><small>restants sur ${k.stock}</small></span>` : html`<span><b>${v.perks.reduce((n, x) => n + x.total, 0)}</b><small>en tout</small></span>`}</div>
    ${k?.stock != null ? bar(k.stock_used, k.stock) : ''}
    ${v.uses.length ? html`<p class="eyebrow">Dernières validations</p>
      <ul class="ce-rows qx-uses">${v.uses.slice(0, 15).map(u => html`<li><span><strong>${fmtWhen(u.used_at)}</strong>${v.perks.length > 1 ? html`<br><span class="muted small">${u.title}</span>` : ''}
        <br><span class="muted small">${how(u.method)} ${partnerWho(u)}</span></span><b class="mono">code ${u.code}</b></li>`)}</ul>` : ''}
    <p class="hint">Le prénom du membre s’affiche au moment de valider, pour vérifier ; il n’est pas gardé ici. Le club a le détail.</p></section>`;
}
